/**
 * Authenticated dashboard navigation and account controls.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileText,
  ListChecks,
  NotebookPen,
  Sparkles,
  Users,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useClinicalTrialsAdminAccess } from "../hooks/useClinicalTrialsAdminAccess";
import { resolveAvatar } from "../utils/userAvatar";
import { clearStoredNotificationDate } from "../utils/notificationDate";
import { clearStoredNotificationFilters } from "../utils/notificationFilterState";
import { NotificationBellIcon, NotificationBellIconFilled } from "../svg/notificationBellIcon";
import { RunStackIcon } from "../svg/runStackIcon";
import { LogoutIcon } from "../svg/logoutIcon";
import { RisaLogoWhite } from "../svg/risaLogoWhite";

/**
 * Sidebar tab, matching the medonc rail model (`id` / `label` /
 * `defaultIcon` / `selectedIcon`) plus a route path and a short rail caption.
 */
interface NavItem {
  id: string;
  /** Full name, used as the hover tooltip and accessible title. */
  label: string;
  /** Short caption rendered under the icon, sized for the narrow rail. */
  railLabel: string;
  path: string;
  defaultIcon: JSX.Element;
  selectedIcon: JSX.Element;
  /** Internal-only surface: a client-facing user never sees this tab. */
  superAdminOnly?: boolean;
  /** Notes is also available to Clinical Trials admins. */
  clinicalTrialsAdminOnly?: boolean;
}

/** Rail surface: matches the medonc dashboard's dark vertical gradient. */
const RAIL_BACKGROUND =
  "linear-gradient(180deg, #090E17 0%, #090E16 3.88%, #010101 100%)";

/** Icon plate shared by the nav tabs — white at 15% opacity when active/hovered. */
const ICON_PLATE =
  "flex h-9 w-9 items-center justify-center rounded-md text-white";

/**
 * Expanding left navigation rail for notifications, notes, ingestion, and
 * settings. It opens only from the far-left hover target or keyboard focus and
 * expands in normal layout flow so page content moves aside. Navigation and
 * logout are wired to react-router and the shared auth-wrapper session.
 *
 * Visually it follows the medonc dashboard rail: gradient surface, RISA
 * wordmark, and icon-over-caption tabs whose plate highlights on hover/active.
 *
 * @returns The sidebar navigation element
 */
export function Sidebar() {
  const { currentUser } = useUser();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = Boolean(user?.isSuperAdmin);
  const canAccessNotes = useClinicalTrialsAdminAccess();

  // The account badge follows the authenticated user; the CRC_TEAM default is
  // only a fallback for when the auth user has not resolved yet (e.g. tests).
  const avatar = resolveAvatar(user, {
    initials: currentUser.initials,
    name: currentUser.name,
    color: currentUser.color,
  });
  const profilePicUrl =
    currentUser.profilePicUrl?.trim() || user?.profilePicUrl?.trim() || "";
  const [profilePicFailed, setProfilePicFailed] = useState(false);

  useEffect(() => {
    setProfilePicFailed(false);
  }, [profilePicUrl]);

  const showProfilePic = Boolean(profilePicUrl) && !profilePicFailed;

  /**
   * Clear notification preferences before ending the authenticated session.
   *
   * @returns Nothing
   */
  const handleLogout = useCallback(() => {
    clearStoredNotificationDate();
    clearStoredNotificationFilters();
    logout?.();
  }, [logout]);

  const allNavItems: NavItem[] = [
    {
      id: "notifications",
      label: "Patient Notifications",
      railLabel: "Alerts",
      path: "/notifications",
      defaultIcon: <NotificationBellIcon />,
      selectedIcon: <NotificationBellIconFilled />,
      superAdminOnly: true,
    },
    {
      id: "eligibility",
      label: "Eligibility Notifications",
      railLabel: "Eligibility",
      path: "/eligibility",
      defaultIcon: <ListChecks size={24} />,
      selectedIcon: <ListChecks size={24} fill="currentColor" />,
    },
    {
      id: "notes",
      label: "Notes",
      railLabel: "Notes",
      path: "/notes",
      defaultIcon: <NotebookPen size={24} />,
      selectedIcon: <NotebookPen size={24} fill="currentColor" />,
      clinicalTrialsAdminOnly: true,
    },
    {
      id: "manual-summarization",
      label: "Manual Summarization",
      railLabel: "Summarize",
      path: "/manual-summarization",
      defaultIcon: <RunStackIcon />,
      selectedIcon: <RunStackIcon />,
      superAdminOnly: true,
    },
    {
      id: "cohort-screening",
      label: "Cohort Screening",
      railLabel: "Cohort",
      path: "/cohort-screening",
      defaultIcon: <Users size={24} />,
      selectedIcon: <Users size={24} fill="currentColor" />,
      superAdminOnly: true,
    },
    {
      id: "protocols",
      label: "Protocols",
      railLabel: "Protocols",
      path: "/protocols",
      defaultIcon: <FileText size={24} />,
      selectedIcon: <FileText size={24} fill="currentColor" />,
      superAdminOnly: true,
    },
    {
      id: "screening",
      label: "Agentic Screening",
      railLabel: "Screening",
      path: "/screening",
      defaultIcon: <Sparkles size={24} />,
      selectedIcon: <Sparkles size={24} fill="currentColor" />,
      superAdminOnly: true,
    },
  ];

  const navItems = allNavItems.filter(
    (item) =>
      (isSuperAdmin || !item.superAdminOnly) &&
      (canAccessNotes || !item.clinicalTrialsAdminOnly),
  );

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div
      className="group relative z-50 h-screen w-2 flex-none transition-[width] duration-200 ease-out hover:w-20 focus-within:w-20"
      data-testid="sidebar-hover-region"
    >
      <aside
        className="absolute inset-y-0 left-0 flex h-screen w-20 -translate-x-full flex-col overflow-hidden px-[0.62rem] py-2 text-white shadow-lg transition-transform duration-200 ease-out group-hover:translate-x-0 group-focus-within:translate-x-0"
        style={{ background: RAIL_BACKGROUND }}
      >
        <div className="flex h-full flex-col gap-6">
          <div className="sidebar--logo flex items-center justify-center">
            <RisaLogoWhite height="52" width="52" />
          </div>

          <nav className="sidebar--nav flex flex-1 flex-col gap-5 overflow-hidden">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`flex cursor-pointer flex-col items-center transition-all duration-300 ease-in-out ${active ? "" : "hover:scale-105"
                    }`}
                  title={item.label}
                >
                  <div
                    className={`${ICON_PLATE} ${active ? "bg-[#FFFFFF26]" : "hover:bg-[#FFFFFF26]"
                      }`}
                  >
                    {active ? item.selectedIcon : item.defaultIcon}
                  </div>
                  <div
                    className={`mt-1 whitespace-nowrap text-tiny ${active
                      ? "font-semibold text-white"
                      : "font-normal text-primaryGray-10"
                      }`}
                  >
                    {item.railLabel}
                  </div>
                </button>
              );
            })}
          </nav>

          <div className="sidebar--actions mb-6 flex flex-col items-center justify-center gap-6">
            {/* Settings owns profile, password, and reset-2FA controls for every authenticated user. */}
            <button
              onClick={() => navigate("/settings")}
              className="cursor-pointer hover:scale-105"
              title={avatar.name ? `${avatar.name} — Settings` : "Settings"}
            >
              <div
                className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-tiny font-semibold text-white"
                style={{
                  backgroundColor: showProfilePic ? undefined : avatar.color,
                }}
                aria-label={avatar.name || "Account"}
              >
                {showProfilePic ? (
                  <img
                    src={profilePicUrl}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setProfilePicFailed(true)}
                  />
                ) : (
                  avatar.initials
                )}
              </div>
            </button>

            <button
              onClick={handleLogout}
              className="cursor-pointer text-white hover:scale-105"
              title="Logout"
            >
              <LogoutIcon />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
